import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosRequestHeaders } from "axios";
import API, { apis } from "common/apis";
import { IRequisitionUserMappingUpload } from "common/interface";
import { IApiError } from "common/interface/IApiError";
import { IPpdDeliveryOrdersApi } from "common/interface/IPpdDeliveryOrders";
import { IPpdOrders, IPpdOrdersApi } from "common/interface/IPpdOrders";
import { IAvailableOrdersApi } from "common/interface/IAvailableOrders";
import { IPickerMappingUpload as IPickerMappingUpload } from "common/interface/IPickerUploadScheme";
import { logoutIfNeeded } from "common/utils";
import { getPersistedProcessingPickerMapping, persistProcessingPickerMapping, removePersistedProcessingPickerMapping } from "services/PersistService/PersistPickerService";
import { RootState } from "store/store";
import { PpdInfo, TableInfo } from "common/interface/TableInfo";
import { IStoreConfig } from "common/interface/IStoreConfig";
import { ICluster } from "common/interface/ICluster";

export interface State {
    status: "loading" | "success" | "error" | "idle" | "cancelling" | "saving";
    error?: string;
    processingPickerMapping: IPickerMappingUpload;
    ppdOrders?: IPpdOrdersApi;
    ppdDeliveryOrders?: IPpdDeliveryOrdersApi;
    availableOrders?: IAvailableOrdersApi | null;
    availableOrdersConfig?: IAvailableOrdersApi | null;
    ppdTableInfo?: PpdInfo;
    storeConfig?: IStoreConfig;
    clusters?: ICluster[];
}

const initialState: State = {
    status: "idle",
    processingPickerMapping: getPersistedProcessingPickerMapping(),
};

export const savePickerMapping = createAsyncThunk<IRequisitionUserMappingUpload>("ppd/picker-packer/upload/save", async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { ppd: { processingPickerMapping: { key } } } = state;

    const response = await API.post(apis.savePickerMapping, {}, {
        params: { key }
    })
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const cancelProcessingPickerMapping = createAsyncThunk("ppd/picker-packer/upload/cancel", async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { ppd: { processingPickerMapping: { key } } } = state;

    const response = await API.post(apis.savePickerMapping, {}, { //FixMe: Get Cancel API
        params: { key, cancel: 1 }
    })
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const cronRemapUsers = createAsyncThunk<{}, AxiosRequestHeaders>("ppd/cron/remap-users", async (headers, thunkApi) => {
    const response = await API.post(apis.cronRemapUsers, {}, { headers: headers }).then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

});

export const genaratePpdSequence = createAsyncThunk<{}, { isManualCall: boolean }>("ppd/generate-sequence", async (params, thunkApi) => {
    const response = await API.post(apis.generateSequence, {}, {
        params: params
    }).then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const getPpdOrderDetails = createAsyncThunk<IPpdOrders, string>("ppd/orders/id", async (id, thunkApi) => {
    const response = await API.get(`${apis.ppdOrder}/${id}`).then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });
    return response;
});

export const getPpdStoreTables = createAsyncThunk<PpdInfo, {storeId?: string, cityId?: string}>("ppd/table", async (params, thunkApi) => {
    const response = await API.get(`${apis.getPpdTableInfo}`, {
        params: params,
    }).then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });
    return response;
});

export const saveTableInfo = createAsyncThunk<number, TableInfo & { storeId: string }>("ppd/table/add", async (tableInfo, thunkApi) => {
    if (tableInfo.qa == undefined) {
        tableInfo.qa = 1;
    }
    const response = await API.put(`${apis.savePpdTableInfo}`, tableInfo).then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });
    return response;
});

export const removeTableInfo = createAsyncThunk<number, number>("ppd/table/remove", async (tableId, thunkApi) => {

    const response = await API.put(`${apis.savePpdTableInfo}/${tableId}/inactive`).then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });
    return response;
});

export const getConsumerClusters = createAsyncThunk<ICluster[], string>("/ppd/clusters", async (storeId, thunkApi) => {
    try {
        const response = await API.get(apis.getClusters, {
            params: { storeId },
        });
        return response.data;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
    }
});

export const getPpdDeliveryOrderDetails = createAsyncThunk<IPpdOrders, string>("ppd/delivery/orders/id", async (id, thunkApi) => {
    const response = await API.get(`${apis.ppdDeliveryOrders}/${id}`).then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });
    return response;
});

export const getPpdOrders = createAsyncThunk<
    IPpdOrdersApi,
    { pageNo: number; pageSize: number; filter: string }
>("/ppd/orders", async ({ pageNo = 1, pageSize = 25, filter }, thunkApi) => {
    const filterParams = filter
        ? Object.fromEntries(new URLSearchParams(filter))
        : "";
    const response = await API.get(apis.ppdOrders, {
        params: {
            pageNo,
            pageSize,
            ...filterParams,
        },
    })
        .then((success) => success.data)
        .catch((error) => {
            const errorMessage =
                error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const cancelPpdOrder = createAsyncThunk("/ppd/order/cancel", async (data: any, thunkApi) => {
    const id = data["orderId"];
    const response = await API.post(apis.cancelPpdOrder + id + "/cancel", data)
        .then(success => success.data as IPpdOrders)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });
    return response;
});

export const getPpdDeliveryOrders = createAsyncThunk<
    IPpdDeliveryOrdersApi,
    { pageNo: number; pageSize: number, filter: string }
>("/ppd/delivery/orders", async ({ pageNo = 1, pageSize = 25, filter }, thunkApi) => {
    const filterParams = filter
        ? Object.fromEntries(new URLSearchParams(filter))
        : "";
    const response = await API.get(apis.ppdDeliveryOrders, {
        params: {
            pageNo,
            pageSize,
            ...filterParams,
        },
    })
        .then((success) => success.data)
        .catch((error) => {
            const errorMessage =
                error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const getAvailableOrders = createAsyncThunk<
    IAvailableOrdersApi,
    { deliveryDate: string; storeId?: string; slotId?: string }
>("/ppd/order/slots/availability", async ({ deliveryDate, storeId, slotId }, thunkApi) => {
    const params: any = { deliveryDate };
    if (storeId) params.storeId = storeId;
    if (slotId) params.slotId = slotId;

    const response = await API.get(apis.getAvailableOrders, {
        params,
    })
        .then((success) => {
            // API returns array directly, wrap it in object for consistency
            const data = success.data;
            return Array.isArray(data) ? data : (data.data || []);
        })
        .catch((error) => {
            const errorMessage =
                error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const updateAvailableCount = createAsyncThunk<
    any,
    { deliveryDate: string; storeId: string; slotId: number; availableCount: number }
>("ppd/update-available-count", async ({ deliveryDate, storeId, slotId, availableCount }, thunkApi) => {
    const payload = {
        deliveryDate,
        storeId,
        slotId,
        availableCount,
    };

    const response = await API.post(apis.updateAvailableCount, payload)
        .then((success) => success.data)
        .catch((error) => {
            const errorMessage =
                error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const getAvailableOrdersConfig = createAsyncThunk<
    IAvailableOrdersApi,
    { storeId?: string; slotId?: string }
>("ppd/get-available-orders-config", async ({ storeId, slotId }, thunkApi) => {
    const params: any = {};
    if (storeId) params.storeId = storeId;
    if (slotId) params.slotId = slotId;

    const response = await API.get(apis.getAvailableOrdersConfig, { params })
        .then((success) => success.data)
        .catch((error) => {
            const errorMessage =
                error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    // Handle both array and paginated responses
    const data = response;
    return Array.isArray(data) ? data : (data.data || []);
});

export const updateAvailableOrdersConfig = createAsyncThunk<
    any,
    { storeId: string; slotId: number; availableCount: number }
>("ppd/update-available-orders-config", async ({ storeId, slotId, availableCount }, thunkApi) => {
    const payload = {
        storeId,
        slotId,
        availableCount,
    };

    const response = await API.post(apis.updateAvailableOrdersConfig, payload)
        .then((success) => success.data)
        .catch((error) => {
            const errorMessage =
                error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const getStoreConfig = createAsyncThunk<IStoreConfig, string>("ppd/store-config", async (storeId, thunkApi) => {
    const response = await API.get(`${apis.getStoreConfig}/${storeId}`)
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });
    return response;
});

export const createStoreConfig = createAsyncThunk<IStoreConfig, IStoreConfig>("ppd/store-config/create", async (config, thunkApi) => {
    const response = await API.post(apis.updateStoreConfig, config)
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });
    return response;
});

export const updateStoreConfig = createAsyncThunk<IStoreConfig, IStoreConfig>("ppd/store-config/update", async (config, thunkApi) => {
    const response = await API.put(`${apis.updateStoreConfig}/${config.storeId}`, config)
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });
    return response;
});

const ppdSlice = createSlice({
    name: "ppd",
    initialState,
    reducers: {
        setProcessingPickerMapping: (state, action) => {
            state.status = "idle";
            state.error = "";
            state.processingPickerMapping = action.payload;
            state.processingPickerMapping.timeUploaded = new Date().toString();
            persistProcessingPickerMapping(state.processingPickerMapping);
        },
        removeProcessingPickerMapping: (state) => {
            state.processingPickerMapping = {};
            state.status = "idle";
            state.error = "";
            removePersistedProcessingPickerMapping();
        },
        removePpdTableInfo: (state) => {
            state.ppdTableInfo = undefined;
            state.clusters = undefined;
            state.status = "idle";
            state.error = "";
        },
        removeStoreConfig: (state) => {
            state.storeConfig = undefined;
            state.status = "idle";
            state.error = "";
        },
    },
    extraReducers(builder) {
        builder
            /**
            * SAVE REQUISITION USER MAPPING
            **/
            .addCase(savePickerMapping.pending, state => {
                state.status = "loading";
            })
            .addCase(savePickerMapping.fulfilled, state => {
                state.status = "success";
                state.error = "";
                state.processingPickerMapping = {};
                removePersistedProcessingPickerMapping();
            })
            .addCase(savePickerMapping.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * CANCEL PROCESSING REQUISITION
            **/
            .addCase(cancelProcessingPickerMapping.pending, state => {
                state.status = "cancelling";
            })
            .addCase(cancelProcessingPickerMapping.fulfilled, state => {
                state.status = "idle";
                state.error = "";
                state.processingPickerMapping = {};
                removePersistedProcessingPickerMapping();
            })
            .addCase(cancelProcessingPickerMapping.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })
            /**
             * CRON PPD REMAP USERS
             */
            .addCase(cronRemapUsers.pending, state => {
                state.status = "loading";
            })
            .addCase(cronRemapUsers.fulfilled, state => {
                state.status = "success";
                state.error = "";
            })
            .addCase(cronRemapUsers.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GENERATE PPD SEQUENCE
             */
            .addCase(genaratePpdSequence.pending, state => {
                state.status = "loading";
            })
            .addCase(genaratePpdSequence.fulfilled, state => {
                state.status = "success";
                state.error = "";
            })
            .addCase(genaratePpdSequence.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GET PPD Orders
             **/
            .addCase(getPpdOrders.pending, state => {
                state.status = "loading";
            })
            .addCase(getPpdOrders.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                state.ppdOrders = action.payload;
            })
            .addCase(getPpdOrders.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GET PPD Delivery Orders
             **/
            .addCase(getPpdDeliveryOrders.pending, state => {
                state.status = "loading";
            })
            .addCase(getPpdDeliveryOrders.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                state.ppdDeliveryOrders = action.payload;
            })
            .addCase(getPpdDeliveryOrders.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GET Available Orders
             **/
            .addCase(getAvailableOrders.pending, state => {
                state.status = "loading";
            })
            .addCase(getAvailableOrders.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                state.availableOrders = action.payload;
            })
            .addCase(getAvailableOrders.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * UPDATE Available Count
             **/
            .addCase(updateAvailableCount.pending, state => {
                state.status = "saving";
            })
            .addCase(updateAvailableCount.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                // Update the availableOrders array with the updated count
                if (state.availableOrders && Array.isArray(state.availableOrders)) {
                    const updatedOrders = state.availableOrders.map(order => {
                        if (order.slotId === action.meta.arg.slotId &&
                            order.entityId === action.meta.arg.storeId &&
                            order.deliveryDate === action.meta.arg.deliveryDate) {
                            return { ...order, availableCount: action.meta.arg.availableCount };
                        }
                        return order;
                    });
                    state.availableOrders = updatedOrders;
                }
            })
            .addCase(updateAvailableCount.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GET Available Orders Config
             **/
            .addCase(getAvailableOrdersConfig.pending, state => {
                state.status = "loading";
            })
            .addCase(getAvailableOrdersConfig.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                state.availableOrdersConfig = action.payload;
            })
            .addCase(getAvailableOrdersConfig.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * UPDATE Available Orders Config
             **/
            .addCase(updateAvailableOrdersConfig.pending, state => {
                state.status = "saving";
            })
            .addCase(updateAvailableOrdersConfig.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                // Update the availableOrdersConfig array with the updated count
                if (state.availableOrdersConfig && Array.isArray(state.availableOrdersConfig)) {
                    const updatedOrders = state.availableOrdersConfig.map(order => {
                        if (order.slotId === action.meta.arg.slotId &&
                            order.entityId === action.meta.arg.storeId) {
                            return { ...order, availableCount: action.meta.arg.availableCount };
                        }
                        return order;
                    });
                    state.availableOrdersConfig = updatedOrders;
                }
            })
            .addCase(updateAvailableOrdersConfig.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GET PPD Table Info
             **/
            .addCase(getPpdStoreTables.pending, state => {
                state.status = "loading";
            })
            .addCase(getPpdStoreTables.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                state.ppdTableInfo = action.payload;
            })
            .addCase(getPpdStoreTables.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error"; 
                state.ppdTableInfo = { storeId: parseInt(action.meta.arg.storeId!), tableInfo: [], };
                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * Save Table Info
             **/
            .addCase(saveTableInfo.pending, state => {
                state.status = "loading";
            })
            .addCase(saveTableInfo.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                let temp = state.ppdTableInfo?.tableInfo ?? [];
                var index = temp.findIndex((e) => e.id == action.meta.arg.id);
                if (index != -1) {
                    temp[index] = action.meta.arg;
                } else {
                    temp.push({ ...action.meta.arg, id: action.payload });
                }
                // }
                state.ppdTableInfo = { storeId: state.ppdTableInfo!.storeId!, tableInfo: temp };
            }
            )
            .addCase(saveTableInfo.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * Delete Table Info
             **/
            .addCase(removeTableInfo.pending, state => {
                state.status = "loading";
            })
            .addCase(removeTableInfo.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                let temp = state.ppdTableInfo?.tableInfo ?? [];
                var index = temp.findIndex((e) => e.id == action.meta.arg);
                temp.splice(index, 1);
                state.ppdTableInfo = { storeId: state.ppdTableInfo!.storeId!, tableInfo: temp };
            }
            )
            .addCase(removeTableInfo.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GET Consumer Clusters
             **/
            .addCase(getConsumerClusters.pending, state => {
                state.clusters = undefined;
            })
            .addCase(getConsumerClusters.fulfilled, (state, action) => {
                state.clusters = action.payload;
            })
            .addCase(getConsumerClusters.rejected, state => {
                state.clusters = [];
            })

            /**
             * Cancel PPD Delivery Orders
             **/
            .addCase(cancelPpdOrder.pending, state => {
                state.status = "saving";
            })
            .addCase(cancelPpdOrder.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                const index = Array.from(state.ppdOrders?.data ?? []).findIndex((e) => e.id === action.payload.id);
                if (index > -1) {
                    const tempRows = Array.from(state.ppdOrders?.data ?? []);
                    tempRows[index] = action.payload;
                    state.ppdOrders!.data = tempRows;
                    state.ppdOrders = state.ppdOrders;
                }
            })
            .addCase(cancelPpdOrder.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GET Store Config
             **/
            .addCase(getStoreConfig.pending, state => {
                state.status = "loading";
                state.storeConfig = undefined;
            })
            .addCase(getStoreConfig.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                state.storeConfig = action.payload;
            })
            .addCase(getStoreConfig.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                state.storeConfig = undefined;

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * CREATE Store Config
             **/
            .addCase(createStoreConfig.pending, state => {
                state.status = "saving";
            })
            .addCase(createStoreConfig.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                state.storeConfig = action.payload;
            })
            .addCase(createStoreConfig.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * UPDATE Store Config
             **/
            .addCase(updateStoreConfig.pending, state => {
                state.status = "saving";
            })
            .addCase(updateStoreConfig.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                state.storeConfig = action.payload;
            })
            .addCase(updateStoreConfig.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })
    }
});

export const {
    setProcessingPickerMapping,
    removeProcessingPickerMapping,
    removePpdTableInfo,
    removeStoreConfig,
} = ppdSlice.actions;
export default ppdSlice.reducer; 